import { useCallback, useEffect, useState } from "react";
import { getInvoices } from "features/Invoices/services/InvoiceService";
import { InvoiceStatus } from "features/Invoices/constants/invoice.constants";
import type { Invoice } from "features/Invoices/types/invoice.types";

const ACTIVE_STATUSES = [
  InvoiceStatus.DRAFT,
  InvoiceStatus.ISSUED,
  InvoiceStatus.PARTIALLY_PAID,
  InvoiceStatus.PAID,
].join(",");

/**
 * Whether a payment or advance settlement has already been applied against
 * this invoice. When true, Regenerate Invoice corrects the same invoice's
 * total in place instead of requiring Cancel Invoice first — that's only
 * still required when the active invoice has zero money against it.
 */
export function invoiceHasMoneyMoved(invoice: {
  status: string;
  totalAmount?: number | null;
  outstandingAmount: number;
}): boolean {
  if (
    invoice.status === InvoiceStatus.PARTIALLY_PAID ||
    invoice.status === InvoiceStatus.PAID
  ) {
    return true;
  }
  return (
    invoice.totalAmount != null && invoice.outstandingAmount < invoice.totalAmount
  );
}

/**
 * The source's current active (non-cancelled) invoice, if any. Regenerate
 * Invoice runs whenever this is null, or whenever it has money against it
 * ({@link invoiceHasMoneyMoved}) — it's only rejected (needing Cancel
 * Invoice first) when an active invoice exists with nothing collected yet.
 */
export function useSourceActiveInvoice({
  sourceType,
  sourceId,
  enabled,
}: {
  sourceType: string;
  sourceId: number | null | undefined;
  /** Skip the fetch when neither action would be shown anyway (e.g. status/permission already fail). */
  enabled: boolean;
}): { loading: boolean; activeInvoice: Invoice | null; refetch: () => void } {
  const [loading, setLoading] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!enabled || !sourceId) {
      setActiveInvoice(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getInvoices({
      sourceType,
      sourceId,
      status: ACTIVE_STATUSES,
      limit: 1,
    })
      .then((res) => {
        if (cancelled) return;
        setActiveInvoice(res.success ? (res.data.rows[0] ?? null) : null);
      })
      .catch(() => {
        if (!cancelled) setActiveInvoice(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, sourceId, enabled, version]);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  return { loading, activeInvoice, refetch };
}
